/** Shared tournament constants (server + client). */

export const TOURNAMENT_BRACKET_SIZES = [4, 8, 16, 32] as const;
export type TournamentBracketSize = (typeof TOURNAMENT_BRACKET_SIZES)[number];

export const TOURNAMENT_RECURRENCES = ["hourly", "daily", "weekly", "custom"] as const;
export type TournamentRecurrence = (typeof TOURNAMENT_RECURRENCES)[number];

/** Examples: "30m", "1d", "1 dia", "1h", "1 hora", "1d, 5h, 15m, 15s". Omitted units = 0. */
export function parseRecurrenceInterval(raw: unknown): number | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  let days = 0;
  let hours = 0;
  let minutes = 0;
  let seconds = 0;
  let matched = false;
  for (const part of trimmed.split(",")) {
    const token = part.trim().toLowerCase();
    if (!token) continue;
    const m = token.match(/^(\d+(?:\.\d+)?)\s*(.+)$/);
    if (!m) continue;
    const value = Number(m[1]);
    const unit = m[2].trim();
    if (!Number.isFinite(value) || value < 0) continue;
    if (/^(d|dia|dias|day|days)$/.test(unit)) {
      days += value;
      matched = true;
    } else if (/^(h|hr|hrs|hora|horas|hour|hours)$/.test(unit)) {
      hours += value;
      matched = true;
    } else if (/^(m|min|mins|minuto|minutos|minute|minutes)$/.test(unit)) {
      minutes += value;
      matched = true;
    } else if (/^(s|sec|secs|seg|segs|segundo|segundos|second|seconds)$/.test(unit)) {
      seconds += value;
      matched = true;
    }
  }
  if (!matched) return null;
  const ms = Math.round((((days * 24 + hours) * 60 + minutes) * 60 + seconds) * 1000);
  return ms > 0 ? ms : null;
}

export function formatRecurrenceInterval(raw: string | null | undefined): string {
  if (!raw?.trim()) return "";
  return raw.trim();
}

export const TOURNAMENT_MATCH_WINDOW_MS = 5 * 60 * 1000;
export const TOURNAMENT_TIE_RESTART_MS = 10 * 1000;
export const TOURNAMENT_TOAST_MINUTES = [5, 2] as const;

/** Modes allowed in tournament matches (SpeedRun and Golf excluded). */
export const TOURNAMENT_MODE_IDS = [
  "normal",
  "wasd",
  "superdot",
  "roulette",
  "sandbox",
  "highway",
  "darkness",
  "mirror",
  "snake",
  "combo",
  "colorrush",
  "heat",
  "hunter",
  "pop",
  "counter",
  "aim",
  "hook",
  "tilt",
  "orbit",
  "delay",
  "progression",
] as const;

export type TournamentModeId = (typeof TOURNAMENT_MODE_IDS)[number];

export const TOURNAMENT_MODE_LABELS: Record<TournamentModeId, string> = {
  normal: "NORMAL",
  wasd: "WASD",
  superdot: "SUPERDOT",
  roulette: "ROULETTE",
  sandbox: "SANDBOX",
  highway: "HIGHWAY",
  darkness: "DARKNESS",
  mirror: "MIRROR",
  snake: "SNAKE",
  combo: "COMBO",
  colorrush: "COLOR RUSH",
  heat: "HEAT",
  hunter: "HUNTER",
  pop: "POP",
  counter: "COUNTER",
  aim: "AIM",
  hook: "HOOK",
  tilt: "TILT",
  orbit: "ORBIT",
  delay: "DELAY",
  progression: "PROGRESSION",
};

export type TournamentMatchModeConfig = {
  matchIndex: number;
  modeId: string;
  levelIndex?: number | null;
};

export type TournamentRoundRewards = Record<string, number>;

export function isTournamentBracketSize(n: unknown): n is TournamentBracketSize {
  return typeof n === "number" && (TOURNAMENT_BRACKET_SIZES as readonly number[]).includes(n);
}

export function isTournamentModeId(raw: unknown): raw is TournamentModeId {
  return typeof raw === "string" && (TOURNAMENT_MODE_IDS as readonly string[]).includes(raw);
}

export function isTournamentRecurrence(raw: unknown): raw is TournamentRecurrence {
  return typeof raw === "string" && (TOURNAMENT_RECURRENCES as readonly string[]).includes(raw);
}

export function tournamentModeLabel(modeId: string): string {
  return isTournamentModeId(modeId) ? TOURNAMENT_MODE_LABELS[modeId] : String(modeId).toUpperCase();
}

/** Total matches in a single-elimination bracket. */
export function tournamentMatchCount(bracketSize: number): number {
  return Math.max(0, bracketSize - 1);
}

/** Round number (1 = first round) for a match index in the bracket. */
export function tournamentMatchRound(bracketSize: number, matchIndex: number): number {
  const totalRounds = Math.log2(bracketSize);
  let round = 1;
  let start = 0;
  let count = bracketSize / 2;
  while (count >= 1) {
    if (matchIndex >= start && matchIndex < start + count) return round;
    start += count;
    count /= 2;
    round += 1;
  }
  return Math.ceil(totalRounds);
}

export function tournamentUsesLevelMetric(modeId: string): boolean {
  return modeId === "progression";
}

export function compareTournamentScores(
  modeId: string,
  a: number,
  b: number,
): "a" | "b" | "tie" {
  if (a === b) return "tie";
  if (tournamentUsesLevelMetric(modeId)) return a > b ? "a" : "b";
  return a > b ? "a" : "b";
}

export function parseMatchModes(raw: unknown, bracketSize: number): TournamentMatchModeConfig[] {
  const count = tournamentMatchCount(bracketSize);
  const out: TournamentMatchModeConfig[] = [];
  if (!Array.isArray(raw)) {
    for (let i = 0; i < count; i++) {
      out.push({ matchIndex: i, modeId: "normal" });
    }
    return out;
  }
  const byIndex = new Map<number, TournamentMatchModeConfig>();
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const matchIndex = Math.floor(Number(o.matchIndex));
    const modeId = typeof o.modeId === "string" ? o.modeId.trim() : "";
    if (!Number.isFinite(matchIndex) || matchIndex < 0 || matchIndex >= count) continue;
    if (!isTournamentModeId(modeId)) continue;
    const levelRaw = Math.floor(Number(o.levelIndex) || 0);
    byIndex.set(matchIndex, {
      matchIndex,
      modeId,
      levelIndex: levelRaw >= 1 ? levelRaw : null,
    });
  }
  for (let i = 0; i < count; i++) {
    out.push(byIndex.get(i) ?? { matchIndex: i, modeId: "normal" });
  }
  return out;
}

export function parseRoundRewards(raw: unknown): TournamentRoundRewards {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: TournamentRoundRewards = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const n = Math.floor(Number(v));
    if (Number.isFinite(n) && n >= 0) out[k] = n;
  }
  return out;
}

export function nextRecurrenceStart(
  from: Date,
  recurrence: TournamentRecurrence,
  customInterval?: string | null,
): Date {
  const d = new Date(from.getTime());
  if (recurrence === "hourly") {
    d.setHours(d.getHours() + 1, 0, 0, 0);
    return d;
  }
  if (recurrence === "daily") {
    d.setDate(d.getDate() + 1);
    return d;
  }
  if (recurrence === "weekly") {
    d.setDate(d.getDate() + 7);
    return d;
  }
  if (recurrence === "custom") {
    const ms = parseRecurrenceInterval(customInterval);
    if (ms) return new Date(d.getTime() + ms);
  }
  return d;
}
